/**
 * TrackOtic API Service
 * Centralized API calls for v1 endpoints
 */
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Control Portal APIs (SentinelX Admin)
export const controlApi = {
  // Dashboard
  getDashboardStats: () => axios.get(`${API_BASE}/api/v1/control/dashboard/stats`),
  getRecentAlerts: (params) => axios.get(`${API_BASE}/api/v1/control/dashboard/recent-alerts`, { params }),
  getExpiringLicenses: (params) => axios.get(`${API_BASE}/api/v1/control/dashboard/expiring-licenses`, { params }),
  getDeviceLocations: (params) => axios.get(`${API_BASE}/api/v1/control/dashboard/device-locations`, { params }),
  getCustomerSummary: () => axios.get(`${API_BASE}/api/v1/control/dashboard/customer-summary`),

  // Tenants
  getTenants: (params) => axios.get(`${API_BASE}/api/v1/control/tenants`, { params }),
  createTenant: (data) => axios.post(`${API_BASE}/api/v1/control/tenants`, data),
  getTenant: (id) => axios.get(`${API_BASE}/api/v1/control/tenants/${id}`),
  updateTenant: (id, data) => axios.put(`${API_BASE}/api/v1/control/tenants/${id}`, data),
  getTenantStats: (id) => axios.get(`${API_BASE}/api/v1/control/tenants/${id}/stats`),

  // Locations
  getLocations: (tenantId, params) => axios.get(`${API_BASE}/api/v1/control/tenants/${tenantId}/locations`, { params }),
  createLocation: (tenantId, data) => axios.post(`${API_BASE}/api/v1/control/tenants/${tenantId}/locations`, data),
  importLocations: (tenantId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API_BASE}/api/v1/control/tenants/${tenantId}/locations/import`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  updateLocation: (tenantId, locationId, data) => axios.put(`${API_BASE}/api/v1/control/tenants/${tenantId}/locations/${locationId}`, data),
  moveLocation: (tenantId, locationId, newParentId) => axios.post(`${API_BASE}/api/v1/control/tenants/${tenantId}/locations/${locationId}/move`, null, { params: { new_parent_id: newParentId } }),

  // Users
  getUsers: (tenantId, params) => axios.get(`${API_BASE}/api/v1/control/tenants/${tenantId}/users`, { params }),
  createUser: (tenantId, data) => axios.post(`${API_BASE}/api/v1/control/tenants/${tenantId}/users`, data),
  updateUser: (tenantId, userId, data) => axios.put(`${API_BASE}/api/v1/control/tenants/${tenantId}/users/${userId}`, data),
  getUserRoles: (tenantId, userId) => axios.get(`${API_BASE}/api/v1/control/tenants/${tenantId}/users/${userId}/roles`),
  assignUserRoles: (tenantId, userId, assignments) => axios.put(`${API_BASE}/api/v1/control/tenants/${tenantId}/users/${userId}/roles`, { assignments }),

  // Roles
  getRoles: (tenantId) => axios.get(`${API_BASE}/api/v1/control/tenants/${tenantId}/roles`),
  createRole: (tenantId, data) => axios.post(`${API_BASE}/api/v1/control/tenants/${tenantId}/roles`, data),
  updateRole: (tenantId, roleId, data) => axios.put(`${API_BASE}/api/v1/control/tenants/${tenantId}/roles/${roleId}`, data),
  setRolePermissions: (tenantId, roleId, permissionCodes) => axios.put(`${API_BASE}/api/v1/control/tenants/${tenantId}/roles/${roleId}/permissions`, { permission_codes: permissionCodes }),

  // Permissions
  getPermissions: () => axios.get(`${API_BASE}/api/v1/control/permissions`),

  // Devices
  getDevices: (tenantId, params) => axios.get(`${API_BASE}/api/v1/control/tenants/${tenantId}/devices`, { params }),
  createDevice: (tenantId, data) => axios.post(`${API_BASE}/api/v1/control/tenants/${tenantId}/devices`, data),
  updateDevice: (tenantId, deviceId, data) => axios.put(`${API_BASE}/api/v1/control/tenants/${tenantId}/devices/${deviceId}`, data),
  setDeviceEntitlement: (tenantId, deviceId, data) => axios.put(`${API_BASE}/api/v1/control/tenants/${tenantId}/devices/${deviceId}/entitlement`, data),
  suspendDevice: (tenantId, deviceId) => axios.post(`${API_BASE}/api/v1/control/tenants/${tenantId}/devices/${deviceId}/suspend`),
  resumeDevice: (tenantId, deviceId) => axios.post(`${API_BASE}/api/v1/control/tenants/${tenantId}/devices/${deviceId}/resume`),
  revokeDevice: (tenantId, deviceId) => axios.post(`${API_BASE}/api/v1/control/tenants/${tenantId}/devices/${deviceId}/revoke`),

  // Notifications
  getNotificationEndpoints: (tenantId) => axios.get(`${API_BASE}/api/v1/control/tenants/${tenantId}/notification-endpoints`),
  createNotificationEndpoint: (tenantId, data) => axios.post(`${API_BASE}/api/v1/control/tenants/${tenantId}/notification-endpoints`, data),
  updateNotificationEndpoint: (tenantId, endpointId, data) => axios.put(`${API_BASE}/api/v1/control/tenants/${tenantId}/notification-endpoints/${endpointId}`, data),

  // Alerts (Global)
  getGlobalAlerts: (params) => axios.get(`${API_BASE}/api/v1/control/alerts`, { params }),
  getAlertStats: (params) => axios.get(`${API_BASE}/api/v1/control/alerts/stats`, { params }),

  // Audit
  getAuditLogs: (tenantId, params) => axios.get(`${API_BASE}/api/v1/control/tenants/${tenantId}/audit`, { params }),

  // Integrations
  getIntegrationSummary: (tenantId) => axios.get(`${API_BASE}/api/v1/control/tenants/${tenantId}/integrations/summary`),
  getIntegrationClients: (tenantId, params) => axios.get(`${API_BASE}/api/v1/control/tenants/${tenantId}/integrations/clients`, { params }),
  updateIntegrationClient: (tenantId, clientId, data) => axios.patch(`${API_BASE}/api/v1/control/tenants/${tenantId}/integrations/clients/${clientId}`, data),
  getWebhooks: (tenantId, params) => axios.get(`${API_BASE}/api/v1/control/tenants/${tenantId}/integrations/webhooks`, { params }),
  updateWebhook: (tenantId, webhookId, data) => axios.patch(`${API_BASE}/api/v1/control/tenants/${tenantId}/integrations/webhooks/${webhookId}`, data),
  getWebhookDeliveries: (tenantId, webhookId, params) => axios.get(`${API_BASE}/api/v1/control/tenants/${tenantId}/integrations/webhooks/${webhookId}/deliveries`, { params }),

  // Billing & Licensing
  getBillingSummary: (tenantId) => axios.get(`${API_BASE}/api/v1/control/tenants/${tenantId}/billing/summary`),
  getBillingDashboard: (tenantId) => axios.get(`${API_BASE}/api/v1/control/tenants/${tenantId}/billing`),
  getLicenses: (tenantId, params) => axios.get(`${API_BASE}/api/v1/control/tenants/${tenantId}/billing/licenses`, { params }),
  extendLicense: (tenantId, licenseId, data) => axios.post(`${API_BASE}/api/v1/control/tenants/${tenantId}/billing/licenses/${licenseId}/extend`, data),
  suspendLicense: (tenantId, licenseId) => axios.post(`${API_BASE}/api/v1/control/tenants/${tenantId}/billing/licenses/${licenseId}/suspend`),
  reactivateLicense: (tenantId, licenseId) => axios.post(`${API_BASE}/api/v1/control/tenants/${tenantId}/billing/licenses/${licenseId}/reactivate`),

  // Global Audit
  getGlobalAuditLogs: (params) => axios.get(`${API_BASE}/api/v1/control/audit`, { params }),
  getGlobalAuditStats: (params) => axios.get(`${API_BASE}/api/v1/control/audit/stats`, { params }),
  getAuditEntityTypes: () => axios.get(`${API_BASE}/api/v1/control/audit/entity-types`),
  getAuditActions: () => axios.get(`${API_BASE}/api/v1/control/audit/actions`),

  // Global Devices
  getGlobalDeviceStats: () => axios.get(`${API_BASE}/api/v1/control/devices/stats`),
  getGlobalDevices: (params) => axios.get(`${API_BASE}/api/v1/control/devices`, { params }),
  getGlobalDeviceDetail: (deviceId) => axios.get(`${API_BASE}/api/v1/control/devices/${deviceId}`),
  getDeviceFeatures: (deviceId) => axios.get(`${API_BASE}/api/v1/control/devices/${deviceId}/features`),
  assignDevice: (deviceId, data) => axios.post(`${API_BASE}/api/v1/control/devices/${deviceId}/assign`, data),
  activateDevice: (deviceId, data) => axios.post(`${API_BASE}/api/v1/control/devices/${deviceId}/activate`, data),
  suspendGlobalDevice: (deviceId) => axios.post(`${API_BASE}/api/v1/control/devices/${deviceId}/suspend`),
  resumeGlobalDevice: (deviceId) => axios.post(`${API_BASE}/api/v1/control/devices/${deviceId}/resume`),
  deactivateDevice: (deviceId) => axios.post(`${API_BASE}/api/v1/control/devices/${deviceId}/deactivate`),
  bulkDeviceAction: (data) => axios.post(`${API_BASE}/api/v1/control/devices/bulk-action`, data),

  // Subscription Plans
  getPlans: (params) => axios.get(`${API_BASE}/api/v1/control/plans`, { params }),
  getPlan: (planId) => axios.get(`${API_BASE}/api/v1/control/plans/${planId}`),
  createPlan: (data) => axios.post(`${API_BASE}/api/v1/control/plans`, data),
  updatePlan: (planId, data) => axios.patch(`${API_BASE}/api/v1/control/plans/${planId}`, data),
  deletePlan: (planId) => axios.delete(`${API_BASE}/api/v1/control/plans/${planId}`),

  // Global Billing
  getGlobalBillingSummary: () => axios.get(`${API_BASE}/api/v1/control/billing/summary`),
  getGlobalInvoices: () => axios.get(`${API_BASE}/api/v1/control/billing/invoices`),
  getGlobalSubscriptions: () => axios.get(`${API_BASE}/api/v1/control/billing/subscriptions`),

  // Platform Settings
  getSettings: () => axios.get(`${API_BASE}/api/v1/control/settings`),
  updateSettings: (data) => axios.put(`${API_BASE}/api/v1/control/settings`, data),
  getAlertSettings: () => axios.get(`${API_BASE}/api/v1/control/settings/alerts`),
  getSecuritySettings: () => axios.get(`${API_BASE}/api/v1/control/settings/security`),
  getIntegrationSettings: () => axios.get(`${API_BASE}/api/v1/control/settings/integration`),
};

// Customer Ops Portal APIs
export const opsApi = {
  // Auth
  login: (email, password) => axios.post(`${API_BASE}/api/v1/auth/login`, { email, password }),
  logout: () => axios.post(`${API_BASE}/api/v1/auth/logout`),
  getMe: () => axios.get(`${API_BASE}/api/v1/auth/me`),

  // Feature Entitlements
  getTenantFeatures: () => axios.get(`${API_BASE}/api/v1/features/tenant`),
  getDeviceFeatures: (deviceId) => axios.get(`${API_BASE}/api/v1/features/device/${deviceId}`),

  // Reports
  getReportsSummary: (days = 7) => axios.get(`${API_BASE}/api/v1/reports/summary`, { params: { days } }),
  getTemperatureReports: (days = 7) => axios.get(`${API_BASE}/api/v1/reports/temperature`, { params: { days } }),
  getDeliveryReports: (days = 7) => axios.get(`${API_BASE}/api/v1/reports/delivery`, { params: { days } }),
  getDeviceReports: () => axios.get(`${API_BASE}/api/v1/reports/devices`),
  getAlertReports: (days = 7) => axios.get(`${API_BASE}/api/v1/reports/alerts`, { params: { days } }),

  // Report Exports
  exportReportCSV: (reportType, days = 7) => {
    const token = localStorage.getItem('ops_token');
    window.open(`${API_BASE}/api/v1/reports/export/csv/${reportType}?days=${days}&token=${encodeURIComponent(token)}`, '_blank');
  },
  exportReportPDF: (reportType, days = 7) => {
    const token = localStorage.getItem('ops_token');
    window.open(`${API_BASE}/api/v1/reports/export/pdf/${reportType}?days=${days}&token=${encodeURIComponent(token)}`, '_blank');
  },
  
  // Historical data for charts
  getTemperatureHistory: (days = 7) => axios.get(`${API_BASE}/api/v1/reports/temperature/history`, { params: { days } }),
  getAlertHistory: (days = 7) => axios.get(`${API_BASE}/api/v1/reports/alerts/history`, { params: { days } }),
  getDeviceUptime: (days = 7) => axios.get(`${API_BASE}/api/v1/reports/devices/uptime`, { params: { days } }),

  // Locations (scoped)
  getLocations: (params) => axios.get(`${API_BASE}/api/v1/locations`, { params }),
  getLocationTree: () => axios.get(`${API_BASE}/api/v1/locations/tree`),

  // Work Orders
  getWorkOrders: (params) => axios.get(`${API_BASE}/api/v1/work-orders`, { params }),
  createWorkOrder: (data) => axios.post(`${API_BASE}/api/v1/work-orders`, data),
  getWorkOrder: (id) => axios.get(`${API_BASE}/api/v1/work-orders/${id}`),
  updateWorkOrder: (id, data) => axios.put(`${API_BASE}/api/v1/work-orders/${id}`, data),
  assignWorkOrder: (id, data) => axios.post(`${API_BASE}/api/v1/work-orders/${id}/assign`, data),
  startWorkOrder: (id) => axios.post(`${API_BASE}/api/v1/work-orders/${id}/start`),
  completeWorkOrder: (id) => axios.post(`${API_BASE}/api/v1/work-orders/${id}/complete`),
  cancelWorkOrder: (id) => axios.post(`${API_BASE}/api/v1/work-orders/${id}/cancel`),

  // Tracking & Telemetry
  getLiveTracking: (params) => axios.get(`${API_BASE}/api/v1/tracking/live`, { params }),
  getTelemetry: (params) => axios.get(`${API_BASE}/api/v1/telemetry`, { params }),
  getDeviceHealth: (params) => axios.get(`${API_BASE}/api/v1/devices/health`, { params }),
  getDeviceTelemetry: (deviceId, params) => axios.get(`${API_BASE}/api/v1/telemetry/device/${deviceId}`, { params }),

  // Alerts
  getAlerts: (params) => axios.get(`${API_BASE}/api/v1/alerts`, { params }),
  acknowledgeAlert: (id) => axios.post(`${API_BASE}/api/v1/alerts/${id}/acknowledge`),
  resolveAlert: (id) => axios.post(`${API_BASE}/api/v1/alerts/${id}/resolve`),

  // Reports
  getTripSummary: (workOrderId) => axios.get(`${API_BASE}/api/v1/reports/work-order/${workOrderId}/trip-summary`),
  getTemperatureCompliance: (workOrderId) => axios.get(`${API_BASE}/api/v1/reports/work-order/${workOrderId}/temperature-compliance`),
  exportReport: (workOrderId, format) => axios.get(`${API_BASE}/api/v1/reports/work-order/${workOrderId}/export`, { 
    params: { format },
    responseType: format === 'csv' ? 'blob' : 'json'
  }),
};

// Telemetry Ingestion API (for testing/admin)
export const ingestApi = {
  sendTelemetry: (data) => axios.post(`${API_BASE}/api/v1/ingest/v1/telemetry`, data),
  getDeviceStatus: (deviceUid) => axios.get(`${API_BASE}/api/v1/ingest/v1/device/${deviceUid}/status`),
};

export default { controlApi, opsApi, ingestApi };
